import { ApiService } from './ApiService';
import { StorageCacheService } from './StorageCacheService';
import { IFile } from '@/shared/IFile';

export class CourseFileService {

  /**
   * Get file information with caching
   */
  public static async getFile(
    courseId: string,
    filePath: string,
    force?: boolean
  ): Promise<IFile> {

    const key = `/api/course/${courseId}/files/${filePath}`;

    const cached = StorageCacheService.getValue<IFile>(key);
    if (!force && cached) {
      return cached;
    }

    const file = await ApiService.fetch<IFile>(key);

    StorageCacheService.setValue(key, file);
    return file;
  }

  /**
   * Delete a file
   */
  public static async deleteFile(courseId: string, filePath: string): Promise<void> {

    const key = `/api/course/${courseId}/files/${filePath}`;

    await ApiService.fetch(key, 'DELETE');

    StorageCacheService.resetCache(`/api/course/${courseId}/files`);
  }

  /**
   * Upload files and update their titles in one operation
   */
  public static async uploadFiles(courseId: string, moduleId: string, files: File[], title: string, overwrite: 'fail' | 'rename' | 'replace'): Promise<IFile[]> {

    const formData = new FormData();
    formData.append('overwrite', overwrite); // Default behavior is to rename if file exists
    formData.append('title', title);

    for (const file of files) {
      formData.append('files', file);
    }

    const uploaded = await ApiService.fetch<IFile[]>(
      `/api/course/${courseId}/files/${moduleId}`,
      'POST',
      formData
    );

    for (const file of uploaded) {
      const key = `/api/course/${courseId}/files/${moduleId}/${file.name}`;
      StorageCacheService.setValue(key, file);
    }

    return uploaded;
  }

  public static async getJsonFile(courseId: string, filePath: string): Promise<any> {
    const json = await ApiService.fetch<any>(`/api/course/${courseId}/json/${filePath}`, 'GET');
    return json;
  }

  public static async putJsonFile(courseId: string, filePath: string, data: any): Promise<void> {
    await ApiService.fetch(`/api/course/${courseId}/json/${filePath}`, 'PUT', { data });
  }

  public static async renameFile(courseId: string, moduleId: string, oldFileName: string, newFileName: string, newFileTitle: string): Promise<void> {
    await ApiService.fetch(`/api/course/${courseId}/json/${moduleId}/${oldFileName}`, 'PATCH', { newFileName, newFileTitle });
  }
}